import { UseQueryResult, useQueries, useQuery } from "@tanstack/react-query";

import { Simulation, StatusesListNameEnum } from "@nens/threedi/openapi-client";

import { getSimulationsApi } from "../apiConfiguration";

import { ID } from "./queryClient";

const simulationsApi = getSimulationsApi();

// Hook to fetch a single simulation
export function useSimulation(simulationId: ID) {
  return useQuery({
    queryKey: ["simulation", simulationId],
    queryFn: () => {
      if (!simulationId) throw new Error("simulationId is required");
      return simulationsApi.simulationsRead({ id: simulationId });
    },
    enabled: !!simulationId,
  });
}

// Hook to fetch multiple simulations
// XXX See if typing can be improved using https://blog.johnnyreilly.com/2021/01/03/strongly-typing-react-query-s-usequeries/
export function useSimulations(simulationIds: number[]) {
  return useQueries({
    queries: simulationIds.map((simulationId) => {
      return {
        queryKey: ["simulation", simulationId],
        queryFn: () => simulationsApi.simulationsRead({ id: simulationId }),
      };
    }),
  }) as UseQueryResult<Simulation, unknown>[];
}

export function useThreediModelUsages(threediModelId: number | null) {
  return useQuery({
    queryKey: ["threedimodelUsages", threediModelId],
    queryFn: () => {
      if (threediModelId === null) throw new Error("threediModelId is required");
      return simulationsApi.usageList({
        simulationThreedimodelId: threediModelId,
      });
    },
    enabled: threediModelId !== null,
  });
}

export function useRevisionSimulations(revisionId: number | null) {
  return useQuery({
    queryKey: ["revisionSimulations", revisionId],
    queryFn: () => {
      if (revisionId === null) throw new Error("revisionId is required");
      return simulationsApi.simulationsList({
        threedimodelRevisionId: revisionId,
      });
    },
    enabled: revisionId !== null,
  });
}

// Simulation events
export function useSimulationEvents(simulationId: ID) {
  return useQuery({
    queryKey: ["simulation", "events", simulationId],
    queryFn: () => {
      if (!simulationId) throw new Error("simulationId is required");
      return simulationsApi.simulationsEvents({ id: simulationId });
    },
    enabled: !!simulationId,
  });
}

// Status
export function useSimulationStatus(simulationId: ID) {
  return useQuery({
    queryKey: ["simulation", "status", simulationId],
    queryFn: () => {
      if (!simulationId) throw new Error("simulationId is required");
      return simulationsApi.simulationsStatusList({
        simulationPk: simulationId,
      });
    },
    enabled: !!simulationId,
  });
}
export function useSimulationStatuses(name: StatusesListNameEnum, refetchInterval: number) {
  return useQuery({
    queryKey: ["simulation", "statuses", name],
    queryFn: () =>
      simulationsApi.statusesList({
        name: name,
      }),
    refetchInterval,
  });
}

// Result files
export function useResultFiles(simulationId: ID) {
  return useQuery({
    queryKey: ["results_files", simulationId],
    queryFn: () => {
      if (!simulationId) throw new Error("simulationId is required");
      return simulationsApi.simulationsResultsFilesList({
        simulationPk: simulationId,
      });
    },
    enabled: !!simulationId,
  });
}
